import { Controller } from "@hotwired/stimulus";

const REQUIRED_MESSAGES = {
  name: "Name is required.",
  startDate: "Start date is required.",
  endDate: "End date is required."
};

const DATE_ORDER_MESSAGE = "End date must be on or after start date.";
const NOTICE_MESSAGE = "Please update the highlighted fields.";

export default class extends Controller {
  static targets = [
    "nameField",
    "startDateField",
    "endDateField",
    "nameError",
    "startDateError",
    "endDateError",
  ];

  connect() {
    this.hasAttemptedSubmit = false;
  }

  validate(event) {
    this.hasAttemptedSubmit = true;

    const requiredFieldsValid = this.validateRequiredFields();
    const dateOrderValid = this.validateDateOrder();
    const valid = requiredFieldsValid && dateOrderValid;

    this.updateErrorNotice();

    if (valid) return;

    event.preventDefault();
    this.focusFirstInvalidField();
  }

  handleFieldInput() {
    this.revalidateAfterSubmit();
  }

  handleFieldChange() {
    this.revalidateAfterSubmit();
  }

  revalidateAfterSubmit() {
    if (!this.hasAttemptedSubmit) return;

    this.validateRequiredFields();
    this.validateDateOrder();
    this.updateErrorNotice();
  }

  validateRequiredFields() {
    const nameValid = this.nameFieldTarget.value.trim() !== "";
    const startDateValid = this.startDateFieldTarget.value !== "";
    const endDateValid = this.endDateFieldTarget.value !== "";

    this.toggleFieldError(
      this.nameFieldTarget,
      this.nameErrorTarget,
      nameValid ? "" : REQUIRED_MESSAGES.name
    );
    this.toggleFieldError(
      this.startDateFieldTarget,
      this.startDateErrorTarget,
      startDateValid ? "" : REQUIRED_MESSAGES.startDate
    );
    this.toggleFieldError(
      this.endDateFieldTarget,
      this.endDateErrorTarget,
      endDateValid ? "" : REQUIRED_MESSAGES.endDate
    );

    return nameValid && startDateValid && endDateValid;
  }

  validateDateOrder() {
    const startDate = this.startDateFieldTarget.value;
    const endDate = this.endDateFieldTarget.value;

    if (!startDate || !endDate) return true;

    const endBeforeStart = endDate < startDate;
    this.toggleFieldError(
      this.endDateFieldTarget,
      this.endDateErrorTarget,
      endBeforeStart ? DATE_ORDER_MESSAGE : ""
    );

    return !endBeforeStart;
  }

  toggleFieldError(field, errorTarget, message) {
    const invalid = message.length > 0;
    field.classList.toggle("ec-field-control--invalid", invalid);
    field.setAttribute("aria-invalid", invalid ? "true" : "false");

    errorTarget.textContent = message;
    errorTarget.classList.toggle("hidden", !invalid);
  }

  updateErrorNotice() {
    const notice = this.noticeElement();
    if (!notice) return;

    const messages = [
      this.nameErrorTarget.textContent,
      this.startDateErrorTarget.textContent,
      this.endDateErrorTarget.textContent
    ].map((message) => message.trim()).filter((message) => message.length > 0);

    if (!this.hasAttemptedSubmit || messages.length === 0) {
      notice.textContent = "";
      notice.classList.add("hidden");
      return;
    }

    notice.textContent = NOTICE_MESSAGE;
    notice.classList.remove("hidden");
  }

  focusFirstInvalidField() {
    const firstInvalidField = this.element.querySelector("[aria-invalid='true']:not([disabled])");
    firstInvalidField?.focus();
  }

  noticeElement() {
    if (this._noticeElement) return this._noticeElement;

    const existing = this.element.querySelector("[data-client-validation-notice]");
    if (existing) {
      this._noticeElement = existing;
      return existing;
    }

    const notice = document.createElement("p");
    notice.className = "ec-form-error-notice hidden";
    notice.dataset.clientValidationNotice = "true";
    notice.setAttribute("role", "alert");
    notice.setAttribute("aria-live", "polite");
    notice.setAttribute("aria-atomic", "true");
    this.element.prepend(notice);
    this._noticeElement = notice;
    return notice;
  }
}
